/**
 * WherebyEmbed Component
 * Simple component that renders the Whereby embed element
 * No team selection or permission checking - just renders the embed
 */

import React from 'react'
import { Box } from '@mui/material'
import '@whereby.com/browser-sdk/embed'
import { INTERAX_COLORS } from '../../../constants/colors'

interface WherebyEmbedProps {
  room: string
  displayName: string
  width?: string | number
  height?: string | number
  className?: string
  lang?: string
}

/**
 * WherebyEmbed - Simple wrapper for Whereby embed element
 * Replaces WherebyFlowContainer for simpler use cases without team selection/permissions
 */
export const WherebyEmbed: React.FC<WherebyEmbedProps> = ({
  room,
  displayName,
  width = '100%',
  height,
  className = '',
  lang = 'sv'
}) => {
  return (
    <Box
      className={className}
      sx={{
        width,
        height: '100%'
      }}
    >
      {/* Outer container with heritage blue background */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: '400px',
          p: 2,
          borderRadius: '10px',
          backgroundColor: `${INTERAX_COLORS.heritageBlue}66`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Inner container for Whereby embed */}
        <Box
          sx={{
            width: '100%',
            height: height || '100%',
            p: 1,
            borderRadius: '10px',
            backgroundColor: INTERAX_COLORS.heritageBlue,
            overflow: 'hidden'
          }}
        >
          {/* Keyed on the room: this custom element holds a live RTC session, so changing the attribute
              in place joins the new room while the old one keeps running — one tab in two meetings. */}
          <whereby-embed
            key={room}
            room={room}
            displayName={displayName}
            lang={lang}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              borderRadius: '8px'
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}
